import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

export default function CartDrawer({ open, onClose }) {
  const {
    cart,
    totalQuantity,
    meetsMinimum,
    minQuantity,
    updateQuantity,
    removeFromCart,
    clearCart,
    requestQuote,
  } = useQuote();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useBodyScrollLock(open);

  const shortfall = Math.max(0, minQuantity - totalQuantity);
  const hasGarage = cart.some((it) => it.category === "Garage");
  const hasExterior = cart.some((it) => it.category === "Exterior");
  const hasInterior = cart.some((it) => it.category === "Interior");
  const suggestMatch = hasGarage && !hasExterior ? "exterior" : hasExterior && !hasGarage ? "garage" : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[85] bg-brand-charcoal/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
            className="fixed top-0 right-0 z-[95] h-[100dvh] max-h-[100dvh] w-full sm:w-[420px] md:w-[500px] bg-brand-bone shadow-2xl flex flex-col overflow-hidden"
            data-testid="cart-drawer"
            data-lenis-prevent
          >
            <header className="shrink-0 px-6 py-5 border-b border-brand-green/15 flex items-center justify-between bg-brand-green text-brand-bone">
              <div>
                <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">Atelier</div>
                <h2 className="font-serif text-2xl mt-0.5">
                  Your Cart
                  <span className="ml-2 text-brand-gold text-lg">
                    · {totalQuantity} door{totalQuantity === 1 ? "" : "s"}
                  </span>
                </h2>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center hover:bg-white/10"
                data-testid="cart-close"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-lenis-prevent data-testid="cart-scroll-area">
              {cart.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="text-brand-ink/70 text-sm">
                    Your cart is empty. Configure your first door to get started.
                  </p>
                  <Link
                    to="/collection/exterior"
                    onClick={onClose}
                    className="btn-primary mt-6 justify-center"
                    data-testid="cart-empty-cta"
                  >
                    Explore Exterior Collection →
                  </Link>
                  <p className="mt-4 text-[0.62rem] tracking-[0.22em] uppercase text-brand-ink/40">
                    Minimum order: {minQuantity} doors — any combination of exterior, interior and garage
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-brand-green/10">
                  {cart.map((it) => (
                    <li
                      key={it._uid}
                      className="p-4 flex gap-4"
                      data-testid={`cart-item-${it._uid}`}
                    >
                      <div className="relative w-20 h-24 shrink-0 bg-brand-green/90 flex items-center justify-center overflow-hidden">
                        {it.image ? (
                          <img
                            src={it.image}
                            alt={it.panel_style || "Door"}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                        {it.reference_photo_id && (
                          <span
                            className="absolute top-1 left-1 bg-brand-gold text-brand-green text-[0.5rem] tracking-[0.18em] uppercase font-semibold px-1.5 py-0.5 leading-none shadow-md"
                            data-testid={`cart-ref-photo-badge-${it._uid}`}
                          >
                            Your ref
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-[0.58rem] tracking-[0.24em] uppercase text-brand-gold">
                              {it.series || it.category}
                            </div>
                            <h3 className="font-serif text-lg text-brand-green truncate">
                              {it.panel_style || it.name || "Custom Door"}
                            </h3>
                          </div>
                          <button
                            onClick={() => removeFromCart(it._uid)}
                            className="text-brand-ink/40 hover:text-red-600 shrink-0"
                            aria-label="Remove"
                            data-testid={`cart-remove-${it._uid}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <dl className="mt-1.5 text-[0.68rem] text-brand-ink/70 space-y-0.5">
                          {it.door_type && (
                            <div><span className="text-brand-ink/40 mr-1">Type:</span> {it.door_type}</div>
                          )}
                          {it.material && (
                            <div><span className="text-brand-ink/40 mr-1">Material:</span> {it.material}</div>
                          )}
                          {it.finish && (
                            <div><span className="text-brand-ink/40 mr-1">Finish:</span> {it.finish}</div>
                          )}
                          {it.color && !/after estimate/i.test(it.color) && (
                            <div><span className="text-brand-ink/40 mr-1">Color:</span> {it.color}</div>
                          )}
                          {it.hardware_collection && (
                            <div><span className="text-brand-ink/40 mr-1">Hardware:</span> {it.hardware_collection}</div>
                          )}
                          {(it.width || it.height) && (
                            <div>
                              <span className="text-brand-ink/40 mr-1">Size:</span>
                              {it.width || "—"} × {it.height || "—"}
                            </div>
                          )}
                        </dl>
                        <div className="mt-2 inline-flex items-center border border-brand-ink/15">
                          <button
                            onClick={() =>
                              updateQuantity(it._uid, Math.max(1, Number(it.quantity || 1) - 1))
                            }
                            className="w-7 h-7 flex items-center justify-center text-brand-ink hover:bg-brand-bone-2"
                            aria-label="Decrease"
                            data-testid={`cart-minus-${it._uid}`}
                          >
                            <Minus size={12} />
                          </button>
                          <span
                            className="w-8 text-center text-sm"
                            data-testid={`cart-qty-${it._uid}`}
                          >
                            {it.quantity || 1}
                          </span>
                          <button
                            onClick={() => updateQuantity(it._uid, Number(it.quantity || 1) + 1)}
                            className="w-7 h-7 flex items-center justify-center text-brand-ink hover:bg-brand-bone-2"
                            aria-label="Increase"
                            data-testid={`cart-plus-${it._uid}`}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {suggestMatch && cart.length > 0 && (
                <div
                  className="mx-4 mb-4 border border-brand-gold/40 bg-brand-gold/8 p-4"
                  style={{ background: "rgba(195, 154, 90, 0.08)" }}
                  data-testid={`cart-match-suggestion-${suggestMatch}`}
                >
                  <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
                    A matching touch
                  </div>
                  <p className="mt-1.5 text-sm text-brand-ink leading-snug">
                    {suggestMatch === "exterior"
                      ? "Complete the facade — pair your garage with a coordinating entrance door."
                      : "Extend the design language — add a coordinating garage door to match your entrance."}
                  </p>
                  <Link
                    to={
                      suggestMatch === "exterior"
                        ? "/collection/exterior"
                        : "/collection/garage"
                    }
                    onClick={onClose}
                    className="mt-3 inline-flex items-center gap-2 text-[0.66rem] tracking-[0.24em] uppercase text-brand-green hover:text-brand-gold transition-colors"
                    data-testid={`cart-match-cta-${suggestMatch}`}
                  >
                    Add matching {suggestMatch === "exterior" ? "entrance" : "garage"} door
                    <span aria-hidden>→</span>
                  </Link>
                </div>
              )}
            </div>

            <footer className="shrink-0 border-t border-brand-green/15 p-5 bg-brand-bone">
              {!meetsMinimum && cart.length > 0 && (
                <div
                  className="mb-3 text-[0.68rem] tracking-[0.06em] uppercase text-brand-ink/70 bg-brand-gold/15 border border-brand-gold/30 px-3 py-2"
                  data-testid="cart-min-warning"
                >
                  Add {shortfall} more door{shortfall === 1 ? "" : "s"} to request a quote —
                  minimum order is {minQuantity} doors, any combination of exterior, interior and
                  garage.
                </div>
              )}
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-ink/60">
                  Total doors
                </span>
                <span
                  className="font-serif text-2xl text-brand-green"
                  data-testid="cart-total-qty"
                >
                  {totalQuantity}
                </span>
              </div>
              <button
                onClick={requestQuote}
                disabled={!meetsMinimum}
                className={`btn-primary w-full justify-center ${
                  !meetsMinimum ? "opacity-40 pointer-events-none" : ""
                }`}
                data-testid="cart-request-quote"
              >
                Request Quote →
              </button>
              {cart.length > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm("Empty the cart? This can't be undone.")) clearCart();
                  }}
                  className="mt-3 w-full text-[0.62rem] tracking-[0.24em] uppercase text-brand-ink/50 hover:text-red-600"
                  data-testid="cart-clear"
                >
                  Empty cart
                </button>
              )}
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
